import { Badge, MantineColor, rem } from "@mantine/core";
import { createStyles } from "@mantine/emotion";
import { ReactNode } from "react";
import { statusColors } from "../theme";

const useStyles = createStyles(() => ({
  badge: {
    textTransform: "uppercase",
    fontWeight: 700,
    letterSpacing: "0.05em",
    fontSize: rem(10),
  },
  dot: {
    width: rem(6),
    height: rem(6),
    borderRadius: "50%",
    marginRight: rem(6),
  },
}));

// Common status mappings
const statusMap: Record<string, { label: string; color: string }> = {
  // General statuses
  active: { label: "Active", color: "green" },
  inactive: { label: "Inactive", color: "dark" },
  pending: { label: "Pending", color: "amber" },
  completed: { label: "Completed", color: "green" },
  cancelled: { label: "Cancelled", color: "rose" },
  draft: { label: "Draft", color: "dark" },

  // Tournament specific
  live: { label: "Live", color: "green" },
  upcoming: { label: "Upcoming", color: "cyan" },
  registration: { label: "Registration", color: "purple" },
  in_progress: { label: "In Progress", color: "amber" },
  finished: { label: "Finished", color: "dark" },

  // Event specific
  open: { label: "Open", color: "green" },
  closed: { label: "Closed", color: "dark" },
  sold_out: { label: "Sold Out", color: "rose" },
  waitlist: { label: "Waitlist", color: "amber" },

  // Player specific
  online: { label: "Online", color: "green" },
  offline: { label: "Offline", color: "dark" },
  away: { label: "Away", color: "amber" },
  banned: { label: "Banned", color: "rose" },

  // Payment specific
  paid: { label: "Paid", color: "green" },
  unpaid: { label: "Unpaid", color: "amber" },
  refunded: { label: "Refunded", color: "cyan" },
  failed: { label: "Failed", color: "rose" },

  // Verification
  verified: { label: "Verified", color: "green" },
  unverified: { label: "Unverified", color: "amber" },
  rejected: { label: "Rejected", color: "rose" },
};

export interface StatusBadgeProps {
  status: string;
  label?: string;
  color?: MantineColor;
  size?: "xs" | "sm" | "md" | "lg" | "xl";
  variant?: "filled" | "light" | "outline" | "dot" | "gradient";
  showDot?: boolean;
  icon?: ReactNode;
  className?: string;
}

export const StatusBadge = ({
  status,
  label,
  color,
  size = "sm",
  variant = "light",
  showDot = false,
  icon,
  className,
}: StatusBadgeProps) => {
  const { classes, cx } = useStyles();

  // Normalize status for lookup
  const normalizedStatus = status.toLowerCase().replace(/[\s-]/g, "_");
  const statusConfig = statusMap[normalizedStatus];

  // Determine final values
  const finalLabel = label ?? statusConfig?.label ?? status;
  const finalColor =
    color ?? statusConfig?.color ?? statusColors[normalizedStatus] ?? "dark";

  return (
    <Badge
      className={cx(classes.badge, className)}
      color={finalColor}
      size={size}
      variant={variant}
      leftSection={
        showDot ? (
          <span
            className={classes.dot}
            style={{ backgroundColor: "currentColor" }}
          />
        ) : (
          icon
        )
      }
    >
      {finalLabel}
    </Badge>
  );
};

// Pre-configured status badge variants
export const LiveBadge = (props: Partial<StatusBadgeProps>) => (
  <StatusBadge status="live" showDot {...props} />
);

export const ActiveBadge = (props: Partial<StatusBadgeProps>) => (
  <StatusBadge status="active" {...props} />
);

export const PendingBadge = (props: Partial<StatusBadgeProps>) => (
  <StatusBadge status="pending" {...props} />
);

export const CompletedBadge = (props: Partial<StatusBadgeProps>) => (
  <StatusBadge status="completed" {...props} />
);

export const DraftBadge = (props: Partial<StatusBadgeProps>) => (
  <StatusBadge status="draft" {...props} />
);

export const OnlineBadge = (props: Partial<StatusBadgeProps>) => (
  <StatusBadge status="online" showDot {...props} />
);

export const ErrorBadge = (props: Partial<StatusBadgeProps>) => (
  <StatusBadge status="failed" color="rose" {...props} />
);
